import { useEffect, useMemo, useRef, useState } from "react";
import { useAccess } from "../api/access";
import { api } from "../api/client";
import {
  deriveSpecies,
  isManuallyTagged,
  type Analysis,
  type Shot,
  type Species,
} from "../api/types";
import { SPECIES_LABEL, SPECIES_ORDER } from "./format";
import { LikesAndComments, type SocialTarget } from "./SocialCore";

interface Props {
  imageId: string;
  /** "compact" sits inside an ImageCard, "full" sits in the Lightbox. */
  variant: "compact" | "full";
  /** Current analysis — drives the Tag… picker's selected state. */
  analysis: Analysis;
  /** Called with the refreshed shot after a manual tag change. */
  onChanged?: (shot: Shot) => void;
}

/**
 * Likes, comments and the species Tag… picker on a trail-camera image.
 *
 * <p>The like and comment half lives in {@link LikesAndComments}, shared with
 * the Forum. What is specific to an image is the Tag… picker, which is passed in
 * as the extra control: correcting a species is a statement about a photograph,
 * and there is nothing to correct on a Forum Post.
 */
export function LikeCommentBar({ imageId, variant, analysis, onChanged }: Props) {
  // Liking and commenting are open to every signed-in user, including a camera
  // viewer — only the species override is team-members-only.
  const { canOverrideSpecies } = useAccess();
  const [tagOpen, setTagOpen] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  const tagRef = useRef<HTMLDivElement | null>(null);

  // Methods are looked up on `api` at call time rather than captured here, so a
  // test that spies on api.getLikes still intercepts them.
  const target = useMemo<SocialTarget>(
    () => ({
      key: imageId,
      getLikes: () => api.getLikes(imageId),
      setLiked: (liked) => api.setLiked(imageId, liked),
      listComments: () => api.listComments(imageId),
      addComment: (body) => api.addComment(imageId, body),
    }),
    [imageId],
  );

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  // Close the tag menu on an outside click or Escape.
  useEffect(() => {
    if (!tagOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (tagRef.current && !tagRef.current.contains(e.target as Node)) {
        setTagOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTagOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [tagOpen]);

  const onToggleTag = (e: React.MouseEvent) => {
    stop(e);
    setTagOpen((v) => !v);
  };

  const onPickTag = async (e: React.MouseEvent, value: Species | null) => {
    stop(e);
    if (tagBusy) return;
    setTagBusy(true);
    try {
      const updated = await api.setSpeciesOverride(imageId, value);
      onChanged?.(updated);
      setTagOpen(false);
    } finally {
      setTagBusy(false);
    }
  };

  const selectedSpecies = deriveSpecies(analysis);
  const manuallyTagged = isManuallyTagged(analysis);

  return (
    <LikesAndComments
      target={target}
      variant={variant}
      extra={
        canOverrideSpecies ? (
          <div className="social-tag" ref={tagRef}>
            <button
              type="button"
              className={`social-btn social-tag-btn${tagOpen ? " on" : ""}`}
              onClick={onToggleTag}
              disabled={tagBusy}
              aria-haspopup="menu"
              aria-expanded={tagOpen}
              aria-label="Correct species tag"
              title="Correct species tag"
            >
              <TagIcon />
              <span className="social-count">Tag…</span>
            </button>
            {tagOpen && (
              <div className="social-tag-menu" role="menu" onClick={stop}>
                {SPECIES_ORDER.map((sp) => (
                  <button
                    key={sp}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selectedSpecies === sp}
                    className={`social-tag-item${selectedSpecies === sp ? " on" : ""}`}
                    onClick={(e) => onPickTag(e, sp)}
                    disabled={tagBusy}
                  >
                    <span className="social-tag-check">
                      {selectedSpecies === sp ? "✓" : ""}
                    </span>
                    {SPECIES_LABEL[sp]}
                  </button>
                ))}
                {manuallyTagged && (
                  <button
                    type="button"
                    role="menuitem"
                    className="social-tag-item social-tag-revert"
                    onClick={(e) => onPickTag(e, null)}
                    disabled={tagBusy}
                  >
                    <span className="social-tag-check" />
                    Use AI result
                  </button>
                )}
              </div>
            )}
          </div>
        ) : null
      }
    />
  );
}

function TagIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M4 4h7l9 9-7 7-9-9V4z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="1.4" fill="currentColor" />
    </svg>
  );
}
